import { Resource } from '@react-admin/ra-rbac';
import { useSyncExternalStore } from 'react';
import { Admin, Authenticated, CustomRoutes, useGetIdentity } from 'react-admin';
import { HashRouter, Route, useNavigate } from 'react-router-dom';
import { match } from 'ts-pattern';

import { Services } from '@sh4res/bo-common/components';
import { b2bInvestorProfile } from '@sh4res/bo-common/resources/investor_profile';
import { getThemeForEnvironment } from '@sh4res/bo-common/theme';

import { AuthCallback } from '@Components/AuthCallback';
import { AuthPage } from '@Components/AuthPage';
import { ChangePasswordPageForm } from '@Components/ChangePasswordPageForm';
import { ContactSupport } from '@Components/ContactSupport';
import { FirstConnectionPageForm } from '@Components/FirstConnection';
import { ForgotPasswordPageForm } from '@Components/ForgotPasswordPageForm';
import { Layout } from '@Components/Layout';
import { LoginPageForm } from '@Components/LoginPageForm';
import { MaintenancePage } from '@Components/MaintenancePage';
import { Sitemap } from '@Components/Sitemap';
import { VerifyEmailPageSuccess } from '@Components/VerifyEmailPageSuccess';
import { VerifyPhoneNumberPageForm } from '@Components/VerifyPhoneNumberPageForm';
import { useAnalytics } from '@Hooks/useAnalytics';
import { adminsResource } from '@Resources/admins';
import { campaignsResource } from '@Resources/campaigns';
import { migratedCampaignsResource } from '@Resources/campaigns/migrated';
import { campaignsPayoutsResource } from '@Resources/campaigns/payouts';
import { collectiveOperationsResource } from '@Resources/collective_operations';
import { companiesResource } from '@Resources/companies';
import { CompanyDashboard } from '@Resources/companies/CompanyDashboard';
import { companiesDocumentsResource } from '@Resources/companies/documents';
import { contractFeeChargesResource } from '@Resources/contract_fee_charges';
import { contractsResource } from '@Resources/contracts';
import { dashboardResource } from '@Resources/dashboard';
import { distributorResource } from '@Resources/distributor';
import { DocumentsByCategoryPage } from '@Resources/documents';
import { employeesResource } from '@Resources/employees';
import { individualOperationsResource } from '@Resources/individual_operations';
import { instrumentsResource } from '@Resources/instruments';
import { operationsErResource } from '@Resources/operations_er';
import { ordersResource } from '@Resources/orders';
import { fullyManagedPlanContributionsResource } from '@Resources/orders/fully_managed_plan_contributions';
import { recurringInvestmentsResource } from '@Resources/recurring_investments';
import { savingsFundsRequestsResource } from '@Resources/savings_funds_requests';
import { withdrawalsResource } from '@Resources/withdrawals';
import { maintenanceStore } from '@Utils/maintenanceStore';
import { authProvider } from '@Utils/providers/authProvider';
import { dataProvider } from '@Utils/providers/dataProvider';
import { i18nProvider } from '@Utils/providers/i18nProvider';
import { queryClient } from '@Utils/providers/queryClient';

export const App = () => {
  const isMaintenanceMode = useSyncExternalStore(
    maintenanceStore.subscribe,
    maintenanceStore.getSnapshot,
  );

  if (isMaintenanceMode) {
    return <MaintenancePage />;
  }

  return (
    // react-admin creates its own hash router unless one already exists. Providing it here lets
    // AnalyticsTracker observe navigation on every route, including the pre-login ones.
    <HashRouter>
      <AnalyticsTracker />
      <AdminApp />
    </HashRouter>
  );
};

const AnalyticsTracker = () => {
  useAnalytics();

  return null;
};

const AdminApp = () => (
  <Admin
    theme={getThemeForEnvironment()}
    authProvider={authProvider}
    authCallbackPage={AuthCallback}
    loginPage={
      <AuthPage>
        <LoginPageForm />
      </AuthPage>
    }
    dataProvider={dataProvider}
    i18nProvider={i18nProvider}
    queryClient={queryClient}
    layout={Layout}
    disableTelemetry
    requireAuth
    dashboard={LandingPage}>
    <Resource name="contract_fee_charges" {...contractFeeChargesResource} />
    <Resource name="companies" {...companiesResource} />
    <Resource name="dashboard" {...dashboardResource} />
    <Resource name="employees" {...employeesResource} />
    <Resource name="orders" {...ordersResource} />
    <Resource name="withdrawals" {...withdrawalsResource} />
    <Resource name="instruments" {...instrumentsResource} />
    <Resource name="indexes" />
    <Resource name="savings_funds_requests" {...savingsFundsRequestsResource} />
    <Resource name="contracts" {...contractsResource} />
    <Resource name="campaigns/payouts" {...campaignsPayoutsResource} />
    <Resource name="campaigns/migrated" {...migratedCampaignsResource} />
    <Resource name="campaigns" {...campaignsResource} />
    <Resource name="collective_operations" {...collectiveOperationsResource} />
    <Resource name="operations_er" {...operationsErResource} />
    <Resource name="companies/documents" {...companiesDocumentsResource} />
    <Resource name="investor_profile" {...b2bInvestorProfile} />
    <Resource
      name="orders/fully_managed_plan_contributions"
      {...fullyManagedPlanContributionsResource}
    />
    <Resource name="distributor" {...distributorResource} />
    <Resource name="admins" {...adminsResource} />
    <Resource name="individual_operations" {...individualOperationsResource} />
    <Resource name="recurring_investments" {...recurringInvestmentsResource} />
    <Resource name="recurring_investments/allocations" />
    <CustomRoutes>
      <Route path="/dashboard" element={<CompanyDashboard />} />
      <Route path="/documents" element={<DocumentsByCategoryPage />} />
      <Route path="/services" element={<Services />} />
      <Route path="/contact_support" element={<ContactSupport />} />
      <Route path="/sitemap" element={<Sitemap />} />
    </CustomRoutes>
    <CustomRoutes noLayout>
      <Route
        path="/forgot_password"
        element={
          <AuthPage>
            <ForgotPasswordPageForm />
          </AuthPage>
        }
      />
      <Route
        path="/first_connection"
        element={
          <AuthPage>
            <FirstConnectionPageForm />
          </AuthPage>
        }
      />
      <Route
        path="/verify_phone_number"
        element={
          <Authenticated requireAuth>
            <AuthPage allowLoggedIn>
              <VerifyPhoneNumberPageForm />
            </AuthPage>
          </Authenticated>
        }
      />
      <Route
        path="/change_password"
        element={
          <AuthPage>
            <ChangePasswordPageForm />
          </AuthPage>
        }
      />
      <Route
        path="/verify_email"
        element={
          <AuthPage>
            <VerifyEmailPageSuccess
              header="login_page.messages.verify_email.header"
              subheader="login_page.messages.verify_email.subheader"
              note="login_page.messages.verify_email.note"
            />
          </AuthPage>
        }
      />
    </CustomRoutes>
  </Admin>
);

const LandingPage = () => {
  const { data: identity, isLoading } = useGetIdentity();
  const navigate = useNavigate();

  if (isLoading || !identity) {
    return null;
  }

  match(identity.auth_role)
    .with('b2b_admin', () => navigate('/dashboard'))
    .with('b2b_distributor', () => navigate('/distributor'))
    .with('b2b_inspector', () => navigate('/distributor'))
    .exhaustive();
};
